/**
 * SED-FOUNDATION — evidence-workflow validation + folding helpers.
 *
 * Pure / side-effect-free. The investment-grade report loop is SENSITIVE:
 * we NEVER fabricate or placeholder data here. These helpers only validate
 * what callers pass and surface WARNINGS that the readiness/confidence layer
 * folds in. Per the product decision, open questions WARN and lower the
 * readiness/confidence signal — they never hard-block report generation.
 *
 * What lives here:
 *   1. `validateConfirmedEvidence`     — a confirmed verification MUST carry
 *      `evidence.refIds` + a non-empty `excerpt`; otherwise WARN.
 *   2. `validateSiteVisitNote`         — a site-visit verification requires
 *      evidence (linkedDocIds) + notes (text); otherwise WARN + do-not-verify.
 *   3. `validateSitingNoteEntry`       — per-entry structural validation.
 *   4. `validateWorkflowItem`          — dispatches by `kind`.
 *   5. `shouldKeepWorkflowQuestion`    — stale-question guard: drop a question
 *      once its section is verified in a newer report.
 *   6. `buildWorkflowIdempotencyKey`   — `(reportId, reportVersion,
 *      sedFieldPath)` key so re-ingesting a report version supersedes rather
 *      than duplicates.
 *   7. `dedupeWorkflowItemsByIdempotency` — apply the idempotency key.
 *   8. `computeOpenQuestionPenalty`    — readiness folding term (warn-level).
 *
 * NONE of these mutate their inputs.
 *
 * DEFERRED (out of scope — needs a human checkpoint, do NOT build here):
 *   - TODO(SED-FOUNDATION): rebuild-orchestrator verdict-downgrade-on-error —
 *     wire `validateWorkflowItem` warnings into `sedRebuildOrchestrator.ts` so
 *     a failed verification downgrades the rebuild verdict.
 *   - TODO(SED-FOUNDATION): report-ingestion auto-wiring — fold a generated
 *     report's `FindingClaim` open-questions into `SED.workflows` from
 *     `sedReportIngestion.ts` using `buildWorkflowIdempotencyKey` +
 *     `shouldKeepWorkflowQuestion` for supersession.
 */
import type {
  EvidenceWorkflowItem,
  SitingNoteEntry,
  WorkflowEvidenceRef,
} from '../../types/siteEvidence';

/* ── Warning model ──────────────────────────────────────────────────── */

/** Severity of a workflow warning. All are WARN-level by product decision —
 *  they lower the readiness/confidence signal but never hard-block. */
export type WorkflowWarningSeverity = 'warning' | 'info';

/** Stable codes so callers + tests can branch without string-matching copy. */
export type WorkflowWarningCode =
  | 'confirmed-missing-evidence'
  | 'confirmed-empty-excerpt'
  | 'site-visit-missing-evidence'
  | 'site-visit-missing-notes'
  | 'siting-note-empty-text'
  | 'siting-note-missing-actor'
  | 'open-question-empty-title';

export interface WorkflowWarning {
  code: WorkflowWarningCode;
  severity: WorkflowWarningSeverity;
  /** Human-readable, single-sentence explanation. */
  message: string;
  /** The SED field path the warning pertains to, when known. */
  sedFieldPath?: string;
}

export interface WorkflowValidationResult {
  /** True when no WARNING-severity entries are present. */
  ok: boolean;
  warnings: WorkflowWarning[];
  /**
   * When false, callers MUST NOT flip the item's status to `'confirmed'`.
   * (e.g. a site-visit note with no evidence/notes — warn + leave unverified.)
   */
  canVerify: boolean;
}

function emptyResult(): WorkflowValidationResult {
  return { ok: true, warnings: [], canVerify: true };
}

function hasWarning(result: WorkflowValidationResult): boolean {
  return result.warnings.some((w) => w.severity === 'warning');
}

/* ── 1. Confirmed verification requires evidence ────────────────────── */

/**
 * A confirmed FindingClaim / verification MUST carry evidence: a non-empty
 * `refIds` array AND a non-empty (trimmed) `excerpt`. Missing → WARNING (not
 * a throw) so the caller can fold it into readiness/confidence.
 *
 * Mirrors the `FindingClaim` invariant in `src/lib/findingClaim.ts`
 * (`validateFindingClaimsEpistemic`) but returns structured warnings instead
 * of strings.
 */
export function validateConfirmedEvidence(
  evidence: WorkflowEvidenceRef | undefined,
  sedFieldPath?: string,
): WorkflowValidationResult {
  const result = emptyResult();
  const refIds = evidence?.refIds ?? [];
  const excerpt = (evidence?.excerpt ?? '').trim();

  if (refIds.length === 0) {
    result.warnings.push({
      code: 'confirmed-missing-evidence',
      severity: 'warning',
      message:
        'Confirmed verification is missing evidence reference ids — cannot trace the claim to a source.',
      sedFieldPath,
    });
  }
  if (excerpt.length === 0) {
    result.warnings.push({
      code: 'confirmed-empty-excerpt',
      severity: 'warning',
      message:
        'Confirmed verification is missing a supporting excerpt — no verbatim passage backs the claim.',
      sedFieldPath,
    });
  }

  result.ok = !hasWarning(result);
  // A confirmed verification with no evidence should NOT be treated as verified.
  result.canVerify = result.ok;
  return result;
}

/* ── 2. Site-visit verification requires evidence + notes ───────────── */

/**
 * A site-visit verification requires BOTH evidence (≥1 linked doc id) AND
 * notes (non-empty text). When either is missing the result warns and sets
 * `canVerify = false` so the caller leaves the item unverified.
 *
 * `note` is the strongest single siting note attached to the item (typically
 * the entry the operator authored at apply time).
 */
export function validateSiteVisitNote(
  note: Pick<SitingNoteEntry, 'text' | 'linkedDocIds'> | undefined,
  sedFieldPath?: string,
): WorkflowValidationResult {
  const result = emptyResult();
  const text = (note?.text ?? '').trim();
  const linkedDocIds = note?.linkedDocIds ?? [];

  if (linkedDocIds.length === 0) {
    result.warnings.push({
      code: 'site-visit-missing-evidence',
      severity: 'warning',
      message:
        'Site-visit verification has no linked evidence documents — attach photos, a survey, or a geotech record before marking verified.',
      sedFieldPath,
    });
  }
  if (text.length === 0) {
    result.warnings.push({
      code: 'site-visit-missing-notes',
      severity: 'warning',
      message:
        'Site-visit verification has no observation notes — record what was observed before marking verified.',
      sedFieldPath,
    });
  }

  result.ok = !hasWarning(result);
  result.canVerify = result.ok;
  return result;
}

/* ── 3. Siting-note structural validation ───────────────────────────── */

/**
 * Structural validation of a single `SitingNoteEntry`. `actor` and `text`
 * must be non-empty. When `verificationMethod === 'site-visit'`, the stronger
 * site-visit rule (evidence + notes) also applies and gates `canVerify`.
 */
export function validateSitingNoteEntry(
  entry: SitingNoteEntry,
  sedFieldPath?: string,
): WorkflowValidationResult {
  const result = emptyResult();
  const actor = (entry.actor ?? '').trim();
  const text = (entry.text ?? '').trim();

  if (actor.length === 0) {
    result.warnings.push({
      code: 'siting-note-missing-actor',
      severity: 'warning',
      message: 'Siting note is missing an actor — every entry must be attributable for the audit trail.',
      sedFieldPath,
    });
  }
  if (text.length === 0) {
    result.warnings.push({
      code: 'siting-note-empty-text',
      severity: 'warning',
      message: 'Siting note has empty text — nothing was recorded.',
      sedFieldPath,
    });
  }

  if (entry.verificationMethod === 'site-visit') {
    const visit = validateSiteVisitNote(entry, sedFieldPath);
    result.warnings.push(...visit.warnings);
  }

  result.ok = !hasWarning(result);
  result.canVerify = result.ok;
  return result;
}

/* ── 4. Whole-item dispatch ─────────────────────────────────────────── */

/**
 * Validate a whole `EvidenceWorkflowItem`, dispatching on `kind`. Returns the
 * aggregated warnings + a `canVerify` flag the caller uses to decide whether
 * the item may be flipped to `'confirmed'`.
 *
 * Back-compat: a well-formed item with no problems returns
 * `{ ok: true, warnings: [], canVerify: true }`. An item that is NOT
 * attempting to assert a confirmed/verified state (e.g. an `'open'` question)
 * never warns — the warn-level penalty for an open question is folded
 * separately by `computeOpenQuestionPenalty`, not here.
 */
export function validateWorkflowItem(item: EvidenceWorkflowItem): WorkflowValidationResult {
  switch (item.kind) {
    case 'open-question': {
      const result = emptyResult();
      if ((item.title ?? '').trim().length === 0) {
        result.warnings.push({
          code: 'open-question-empty-title',
          severity: 'warning',
          message: 'Open question has no text.',
          sedFieldPath: item.sedFieldPath,
        });
      }
      result.ok = !hasWarning(result);
      // Open questions never auto-verify — they are unresolved by definition.
      result.canVerify = false;
      return result;
    }
    case 'verification-status': {
      // Only enforce the evidence requirement when the item CLAIMS to be
      // confirmed. A `pending`/`open` verification is allowed to lack
      // evidence (it's in progress); it simply can't be promoted to verified.
      if (item.verificationMethod === 'site-visit') {
        const strongest = pickStrongestSitingNote(item.sitingNotes);
        const visit = validateSiteVisitNote(strongest, item.sedFieldPath);
        if (item.status === 'confirmed') return visit;
        // Not yet confirmed — surface info-level guidance, don't warn.
        return { ok: true, warnings: [], canVerify: visit.canVerify };
      }
      if (item.status === 'confirmed') {
        return validateConfirmedEvidence(item.evidence, item.sedFieldPath);
      }
      return { ok: true, warnings: [], canVerify: false };
    }
    case 'siting-note': {
      const result = emptyResult();
      for (const entry of item.sitingNotes ?? []) {
        const r = validateSitingNoteEntry(entry, item.sedFieldPath);
        result.warnings.push(...r.warnings);
      }
      result.ok = !hasWarning(result);
      result.canVerify = result.ok && (item.sitingNotes?.length ?? 0) > 0;
      return result;
    }
  }
}

/** Best-effort "strongest" siting note for the site-visit gate — the most
 *  recent entry that has the most evidence. Returns `undefined` for an empty
 *  list so `validateSiteVisitNote` can warn. */
function pickStrongestSitingNote(
  notes: SitingNoteEntry[] | undefined,
): SitingNoteEntry | undefined {
  if (!notes || notes.length === 0) return undefined;
  return [...notes].sort((a, b) => {
    const evDelta = (b.linkedDocIds?.length ?? 0) - (a.linkedDocIds?.length ?? 0);
    if (evDelta !== 0) return evDelta;
    return (b.date ?? '').localeCompare(a.date ?? '');
  })[0];
}

/* ── 5. Stale-question guard ────────────────────────────────────────── */

/**
 * Section verification status as seen by a NEWER report. A question raised by
 * an older report should be dropped once the same section comes back
 * `'confirmed'` (verified) in a newer report — otherwise the SED accumulates
 * stale, already-answered questions.
 */
export type SectionVerificationStatus =
  | 'confirmed'
  | 'pending'
  | 'open'
  | 'unknown';

/**
 * Stale-question guard. Returns `true` when the prior open-question should be
 * KEPT, `false` when it should be dropped.
 *
 * Drop rules (return false):
 *   - the section is now `'confirmed'` in a newer report (it's been verified;
 *     the question is answered);
 *   - the prior item was already `'superseded'` or `'dismissed'`.
 *
 * Keep rules (return true):
 *   - the section is still `'open'` / `'pending'` / `'unknown'`;
 *   - the prior item is not a question (verification-status / siting-note are
 *     never dropped by this guard — they carry their own evidence).
 *
 * Pure + deterministic. "Newer" is the caller's responsibility — this guard
 * is only consulted when re-ingesting a newer report version.
 */
export function shouldKeepWorkflowQuestion(
  priorQuestion: Pick<EvidenceWorkflowItem, 'kind' | 'status'>,
  newSectionStatus: SectionVerificationStatus,
): boolean {
  if (priorQuestion.kind !== 'open-question') return true;
  if (priorQuestion.status === 'superseded' || priorQuestion.status === 'dismissed') {
    return false;
  }
  return newSectionStatus !== 'confirmed';
}

/* ── 6/7. Idempotency ───────────────────────────────────────────────── */

/**
 * Idempotency key for a report-sourced workflow item:
 *   `${reportId}__v${reportVersion}__${sedFieldPath}`
 *
 * Re-ingesting the SAME report version produces the SAME key → the item is
 * superseded/replaced rather than duplicated. A NEW report version produces a
 * DIFFERENT key (`v` bumps), so the prior version's items become stale and
 * are dropped by `shouldKeepWorkflowQuestion` when the section is now verified.
 */
export function buildWorkflowIdempotencyKey(
  reportId: string,
  reportVersion: number,
  sedFieldPath: string,
): string {
  return `${reportId}__v${reportVersion}__${sedFieldPath}`;
}

/**
 * De-duplicate workflow items by idempotency key. When two items share a key
 * (a replay of the same report version), the LATER one wins — its
 * `updatedAt`/`createdAt` is newer, so re-ingestion supersedes the prior
 * occurrence instead of duplicating it.
 *
 * Items WITHOUT an `idempotencyKey` (operator-authored, site-visit) are never
 * de-duplicated against each other — they pass through unchanged, keyed by
 * their `id` to drop exact-id replays only.
 *
 * Stable order preserving: the result keeps first-seen ordering, with each
 * key's slot updated in place to the winning item.
 */
export function dedupeWorkflowItemsByIdempotency(
  items: readonly EvidenceWorkflowItem[],
): EvidenceWorkflowItem[] {
  const order: string[] = [];
  const byKey = new Map<string, EvidenceWorkflowItem>();

  const tsOf = (it: EvidenceWorkflowItem): number => {
    const t = Date.parse(it.updatedAt ?? it.createdAt ?? '');
    return Number.isFinite(t) ? t : 0;
  };

  for (const item of items) {
    const key = item.idempotencyKey ?? item.id;
    const existing = byKey.get(key);
    if (!existing) {
      order.push(key);
      byKey.set(key, item);
      continue;
    }
    // Later wins — newest timestamp supersedes.
    if (tsOf(item) >= tsOf(existing)) {
      byKey.set(key, item);
    }
  }

  return order.map((k) => byKey.get(k)!);
}

/* ── 8. Readiness folding term ──────────────────────────────────────── */

export interface OpenQuestionPenaltyInput {
  /** The SED's workflow items (or undefined for a legacy SED). */
  workflows: readonly EvidenceWorkflowItem[] | undefined;
  /**
   * Per-question penalty (0..1 fraction of the readiness scale). Default
   * 0.04 — five unanswered questions shave ~0.20 off the workflow term.
   */
  perQuestion?: number;
  /**
   * Hard cap on the total penalty (0..1). Default 0.20 so the workflow term
   * can never zero out / block the score — it only lowers it (USER DECISION:
   * warn-level, no hard-block).
   */
  cap?: number;
}

export interface OpenQuestionPenalty {
  /** Count of OPEN (unresolved) open-question items. */
  openCount: number;
  /** Count of verification-status items that warn (confirmed but no evidence). */
  unverifiedCount: number;
  /** Penalty as a 0..1 fraction of the readiness scale (already capped). */
  penalty: number;
  /** Aggregated WARN-level reasons for surfacing in the panel. */
  warnings: WorkflowWarning[];
}

/**
 * Compute the warn-level open-questions penalty folded into readiness /
 * confidence. The penalty is ALWAYS subtractive + capped — it lowers the
 * score, never zeroes or blocks it.
 *
 * Counts toward the penalty:
 *   - every `open-question` whose status is `'open'` or `'pending'`;
 *   - every `verification-status` that CLAIMS confirmed but fails
 *     `validateWorkflowItem` (confirmed-without-evidence).
 *
 * A legacy SED (workflows === undefined) yields a zero penalty — identical to
 * today's behaviour.
 */
export function computeOpenQuestionPenalty(
  input: OpenQuestionPenaltyInput,
): OpenQuestionPenalty {
  const perQuestion = input.perQuestion ?? 0.04;
  const cap = input.cap ?? 0.2;
  const workflows = input.workflows ?? [];

  let openCount = 0;
  let unverifiedCount = 0;
  const warnings: WorkflowWarning[] = [];

  for (const item of workflows) {
    if (item.kind === 'open-question') {
      if (item.status === 'open' || item.status === 'pending') {
        openCount += 1;
      }
      continue;
    }
    if (item.kind === 'verification-status' && item.status === 'confirmed') {
      const v = validateWorkflowItem(item);
      if (!v.ok) {
        unverifiedCount += 1;
        warnings.push(...v.warnings);
      }
    }
  }

  const raw = (openCount + unverifiedCount) * perQuestion;
  const penalty = Math.min(cap, Math.max(0, raw));
  return { openCount, unverifiedCount, penalty, warnings };
}
